import React, { useState } from 'react';
import { DateTime } from 'luxon';
import { MealType } from '../../common/commonTypes';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import LoadingButton from '@mui/lab/LoadingButton';
import { deleteFavoriteMeal, deleteMeal } from '../../api';
import { useTranslation } from 'react-i18next';

type ModalDeleteMealType = {
    meal: MealType;
    actionType: string;
    onCancel: () => void;
    onSuccess: () => void;
};

const ModalDeleteMeal = (props: ModalDeleteMealType) => {
    const { meal, actionType, onCancel, onSuccess } = props;
    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const { t, i18n } = useTranslation();

    const confirmDelete = async () => {
        setLoading(true);
        setErrorMessage('');

        try {
            if (!actionType) {
                setErrorMessage('No actionType');
            }

            if (actionType === 'HISTORY') {
                await deleteMeal(meal._id as string);
            }

            if (actionType === 'FAV') {
                await deleteFavoriteMeal(meal._id as string);
            }

            setLoading(false);
            onSuccess();

            window.gtag?.('event', `modalAction - DELETED MEAL FROM ${actionType}`, {});
        } catch (error: any) {
            const message =
                error.response.data.message || error.response.data.error || 'Error deleting meal';
            console.log(
                '%c Error deleting meal: >>> ',
                'background: #900; color: #ffffff',
                error.response.data
            );
            setErrorMessage(message);
            setLoading(false);
        }
    };

    const TEXTS: any = {
        HISTORY: {
            title: t('DELETE_MEAL_title'),
            saved: t('logged_at') + ':',
        },
        FAV: {
            title: t('DELETE_FAV_title'),
            saved: t('created_at') + ':',
        },
    };

    return (
        <Dialog
            open
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    onCancel();
                }
            }}
        >
            <DialogTitle>{TEXTS[actionType].title}</DialogTitle>
            <DialogContent>
                {Boolean(errorMessage) && <Alert severity="error">{errorMessage}</Alert>}
                <div className="text-lg mb-4">{meal.name}</div>
                <div className="text-sm">
                    {TEXTS[actionType].saved}{' '}
                    {DateTime.fromISO(meal.createdAt as string)
                        .setLocale(i18n.language)
                        .toFormat('DD')}
                </div>
            </DialogContent>
            <DialogActions>
                <LoadingButton onClick={onCancel} disabled={loading}>
                    {t('MODAL_cancel')}
                </LoadingButton>
                <LoadingButton
                    onClick={confirmDelete}
                    loading={loading}
                    variant="contained"
                    color="error"
                >
                    {t('MODAL_delete')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default ModalDeleteMeal;
